import { ChevronRightIcon } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/primitives/collapsible";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/shared/components/primitives/sidebar";
import { cn } from "@/shared/lib/utils";

export type ColocaNavMainLeaf = {
  to: string;
  label: string;
  /** Optional trailing badge (e.g. a pending-count pill). Rendered right-aligned inside the item. */
  badge?: ReactNode;
};

export type ColocaNavMainSubItem = ColocaNavMainLeaf & {
  /**
   * Sub-surfaces of one module, behind a disclosure.
   *
   * When present this row is NOT a link — it becomes the collapsible's trigger, and `to` is only its
   * identity. It stays collapsed until one of its children is the active destination.
   *
   * Note the children are UNMOUNTED while closed (base-ui hides the panel by returning null, not by CSS), so
   * a badge on a child does not exist until the row is opened. A count that must be visible from the outside
   * belongs on this row's own `badge`.
   */
  children?: ColocaNavMainLeaf[];
};

export type ColocaNavMainSection = {
  id: string;
  title?: string;
  icon?: ReactNode;
  to?: string;
  defaultOpen?: boolean;
  items?: ColocaNavMainSubItem[];
};

export type ColocaNavMainProps = {
  sections: ColocaNavMainSection[];
  /**
   * The `to` of the item to render active — ALREADY RESOLVED by the caller, which owns the registry.
   *
   * Deliberately a plain string rather than a rule this component applies itself. Matching here was
   * `matchPath({ end: false })` per item, and a prefix match highlights two rows at once the moment one
   * destination nests under another (`/app/crypto-swaps` under `/app/crypto-swaps/history`). It also could
   * only ever compute a per-section winner, since this component renders one group at a time.
   *
   * See `resolveActiveNavTarget` in `@/app/auth/resolveNavAccess`.
   */
  activeTo?: string | null;
};

/**
 * On mobile the sidebar is a Sheet rendered over the page: tapping a destination inside it never triggers the
 * dismiss-on-outside-press, so the panel would stay open on top of the view the user just navigated to.
 * Closing on the gesture (not on a pathname effect) also covers tapping the entry that is already active.
 */
function useCloseOnMobileNavigation() {
  const { isMobile, setOpenMobile } = useSidebar();
  return () => {
    if (isMobile) setOpenMobile(false);
  };
}

function ColocaNavMainLeafRow({ item, isActive }: { item: ColocaNavMainLeaf; isActive: boolean }) {
  const closeOnMobileNavigation = useCloseOnMobileNavigation();
  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton
        isActive={isActive}
        onClick={closeOnMobileNavigation}
        render={<Link to={item.to} />}
      >
        {/*
         * `min-w-0 truncate` is spelled out rather than left to the primitive's
         * `[&>span:last-child]:truncate`: with a badge the badge IS the last child, so the label kept
         * `min-width: auto`, clipped mid-glyph with no ellipsis and pushed the badge past the button's
         * `overflow-hidden` edge.
         */}
        <span className="min-w-0 truncate">{item.label}</span>
        {item.badge != null && <span className="ml-auto flex shrink-0">{item.badge}</span>}
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
}

/**
 * A module row that holds its own sub-surfaces — one disclosure inside the section's disclosure.
 *
 * Collapsed by default, and it opens ITSELF when the route you are on lives inside it: a closed group with an
 * active child would otherwise leave nothing on screen marked, and the user would have no idea where they
 * are. A manual collapse while inside still sticks — the override only lapses when you enter or leave the
 * group, which is the moment the choice stops being about the same thing.
 */
function ColocaNavMainDisclosureRow({
  item,
  activeTo,
}: {
  item: ColocaNavMainSubItem;
  activeTo: string | null;
}) {
  const children = item.children ?? [];
  const containsActive = children.some((child) => child.to === activeTo);

  const [override, setOverride] = useState<boolean | null>(null);
  const [wasInside, setWasInside] = useState(containsActive);
  if (containsActive !== wasInside) {
    // React's documented "adjust state when a prop changes": it re-renders before committing children, so
    // the panel never paints closed for one frame the way an effect would.
    setWasInside(containsActive);
    setOverride(null);
  }
  const open = override ?? containsActive;

  return (
    <Collapsible open={open} onOpenChange={setOverride} render={<SidebarMenuSubItem />}>
      <CollapsibleTrigger
        render={
          <SidebarMenuSubButton
            // `w-full`: unlike the level-0 variant, this one has no width of its own and relies on being an
            // anchor — a <button> would shrink to fit its text and overflow the row.
            // `data-active:bg-transparent` is the level-0 idiom: while collapsed over an active child the
            // rail marker says "you are in here" without the accent fill, which belongs to the real row.
            className="group/disclosure w-full data-active:bg-transparent"
            isActive={containsActive}
            render={<button type="button" />}
          />
        }
      >
        <span className="min-w-0 truncate">{item.label}</span>
        {item.badge != null && <span className="ml-auto flex shrink-0">{item.badge}</span>}
        <ChevronRightIcon
          className={cn(
            "shrink-0 transition-transform group-aria-expanded/disclosure:rotate-90",
            item.badge == null && "ml-auto",
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        {/* `nested`: indents on the left only, so the children keep 142px of label and their badges stay
            in the same column as this row's. */}
        <SidebarMenuSub nested>
          {children.map((child) => (
            <ColocaNavMainLeafRow key={child.to} item={child} isActive={child.to === activeTo} />
          ))}
        </SidebarMenuSub>
      </CollapsibleContent>
    </Collapsible>
  );
}

function ColocaNavMainGroupEntry({
  group,
  activeTo,
}: {
  group: ColocaNavMainSection;
  activeTo: string | null;
}) {
  const subItems = group.items ?? [];
  const subActiveStates = subItems.map((sub) =>
    sub.children?.length ? sub.children.some((child) => child.to === activeTo) : sub.to === activeTo,
  );
  // A section header is not a destination in any current consumer, so this is almost always false; kept for
  // the case where a group is also navigable. No `useLocation` anywhere in here any more: the component now
  // decides nothing about the URL, which is what lets it re-render only when `activeTo` actually changes.
  const groupActive = (group.to != null && group.to === activeTo) || subActiveStates.some(Boolean);
  const closeOnMobileNavigation = useCloseOnMobileNavigation();

  return (
    <Collapsible defaultOpen={group.defaultOpen ?? true} render={<SidebarMenuItem />}>
      <SidebarMenuButton
        tooltip={group.title}
        isActive={groupActive}
        className="data-active:bg-transparent"
        // Only a section header that is itself a destination navigates; as a plain button it just labels the
        // group, so it must not dismiss the sheet.
        onClick={group.to ? closeOnMobileNavigation : undefined}
        render={group.to ? <Link to={group.to} /> : <button type="button" />}
      >
        {group.icon}
        {group.title ? <span>{group.title}</span> : null}
      </SidebarMenuButton>
      {subItems.length ? (
        <>
          <CollapsibleTrigger render={<SidebarMenuAction className="aria-expanded:rotate-90" />}>
            <ChevronRightIcon />
            <span className="sr-only">Toggle</span>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {subItems.map((sub, index) =>
                sub.children?.length ? (
                  <ColocaNavMainDisclosureRow key={sub.to} item={sub} activeTo={activeTo} />
                ) : (
                  <ColocaNavMainLeafRow key={sub.to} item={sub} isActive={subActiveStates[index]} />
                ),
              )}
            </SidebarMenuSub>
          </CollapsibleContent>
        </>
      ) : null}
    </Collapsible>
  );
}

export function ColocaNavMain({ sections, activeTo = null }: ColocaNavMainProps) {
  return (
    <SidebarGroup>
      <SidebarMenu>
        {sections.map((group) => (
          // Keyed by id, never by the translated title — a language switch would remount the group and
          // throw away its collapsed/expanded state.
          <ColocaNavMainGroupEntry key={group.id} group={group} activeTo={activeTo} />
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
